import { HeroContent } from '../../domain/value-objects/HeroContent.vo';
import { InvalidHeroContentError } from '../../domain/errors/InvalidHeroContentError';
import type { HomeContentSource } from '../../domain/repositories/HomeContentSource.interface';
import type { HeroContentDto } from '../dto/HeroContentDto';
import { ApplicationError, type Logger } from '@dma/shared';

/**
 * Use Case per docs/architecture/01-dependency-injection-strategy.md:
 * depends only on the `HomeContentSource` interface (Dependency Inversion),
 * never on a concrete implementation. Wired by the Composition Root.
 */
export class GetHomeHeroContentUseCase {
  constructor(
    private readonly contentSource: HomeContentSource,
    private readonly logger: Logger
  ) {}

  public async execute(): Promise<HeroContentDto> {
    const rawContent = await this.contentSource.getHeroContent();

    try {
      const heroContent = HeroContent.create(rawContent);
      return {
        headline: heroContent.headline,
        subheadline: heroContent.subheadline,
        ctaLabel: heroContent.ctaLabel,
        ctaHref: heroContent.ctaHref
      };
    } catch (error) {
      if (error instanceof InvalidHeroContentError) {
        // Per docs/architecture/05-error-handling-strategy.md: the domain error
        // is logged with full detail here, then re-thrown as a generic,
        // user-safe ApplicationError — the UI never sees the raw domain error.
        this.logger.error('Home hero content failed domain validation', {
          code: error.code,
          message: error.message
        });
        throw new ApplicationError(
          'HOME_HERO_CONTENT_UNAVAILABLE',
          'محتوای صفحه‌ی اصلی در حال حاضر در دسترس نیست.',
          { cause: error }
        );
      }
      throw error;
    }
  }
}
